'use client';

import { type FC, useEffect, useRef } from 'react';
import { Text } from '@kidslabs/mario-nextjs-react-ui-core';
import { useTranslation } from 'next-i18next';
import type { AuthSuccessViewProps } from './auth-success-view.types';
import styles from './auth-success-view.module.css';

const DEFAULT_AUTO_CLOSE_DELAY = 1800;

/**
 * AuthSuccessView - Animated success celebration shown after login/register
 *
 * Displays an animated checkmark with a congratulatory message,
 * then automatically triggers onComplete after a delay.
 * Replaces the form inside the modal for a smooth, delightful transition.
 */
export const AuthSuccessView: FC<AuthSuccessViewProps> = ({
    onComplete,
    autoCloseDelay = DEFAULT_AUTO_CLOSE_DELAY,
}) => {
    const { t } = useTranslation('auth');
    const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    useEffect(() => {
        if (onComplete) {
            timerRef.current = setTimeout(() => {
                onComplete();
            }, autoCloseDelay);
        }

        return () => {
            if (timerRef.current) {
                clearTimeout(timerRef.current);
            }
        };
    }, [onComplete, autoCloseDelay]);

    return (
        <div className={styles.container}>
            <div className={styles.iconWrapper}>
                {/* Pulse ring behind the circle */}
                <div className={styles.pulseRing} />

                {/* Animated circle */}
                <svg className={styles.circle} viewBox="0 0 52 52">
                    <circle cx="26" cy="26" r="25" />
                </svg>

                {/* Animated checkmark */}
                <svg className={styles.checkmark} viewBox="0 0 24 24">
                    <polyline points="4 12 10 18 20 6" />
                </svg>
            </div>

            <div className={styles.content}>
                <Text className={styles.title}>{t('text_login_success', 'Đăng nhập thành công!')}</Text>
                <Text className={styles.subtitle}>
                    {t('text_login_success_subtitle', 'Chào mừng bạn quay trở lại KidsPlaza')}
                </Text>
            </div>

            {/* Subtle progress bar showing auto-close countdown */}
            <div className={styles.progressWrapper}>
                <div
                    className={styles.progressBar}
                    style={{ '--auto-close-duration': `${autoCloseDelay}ms` } as React.CSSProperties}
                />
            </div>
        </div>
    );
};
